// SINGLE SOURCE OF TRUTH for the per-feature upgrade modals.
//
// Every gated feature a user can reach and be turned away from gets an entry here, so the modal
// they see names the feature, names the plan that unlocks it, and says what they get — instead
// of the generic "You don't have access to this feature. Upgrade your account to enjoy access to
// more features!", which tells them neither what to buy nor why.
//
// ⚠ `plan` is the PLAN_FACTS key, NOT the backend plan id. The two disagree on purpose: the plan
// users see as "Pro" is `Studio` internally (see src/app/upgrade/plan-facts.js). Writing 'pro'
// here and letting PLAN_LABEL resolve the display name is what stops a modal telling someone to
// buy "Studio" — a plan name that appears nowhere on the upgrade screen.
//
// `plan` must match the tier the CODE actually checks. `meetsFeaturePlan` derives the gate from
// this field, so an entry looser than the real check promises access the next click still
// refuses.
//
// Copy lives in en/translation.json and is referenced by key — the same arrangement plan-facts.js
// uses for `summaryKey`/`featureKeys`, so these strings are translated into the other nine locales
// like every other (docs/translations.md).
//
// Deliberately NO prices. Live pricing is promo- and A/B-gated (plan-facts.js documents why the
// public page won't render it either), so a figure here would go stale against the checkout. The
// upgrade screen is one tap away and always correct.

// Display name per PLAN_FACTS key. A literal rather than an import, to keep this module free of
// the pricing bundle — but it must not drift from `displayName` there.
export const PLAN_LABEL = {
  plus: 'Plus',
  premium: 'Premium',
  pro: 'Pro',
}

// What each plan is worth beyond the one feature being blocked, so the modal argues for the
// PLAN and not just the click that failed.
//
// These are plan-facts.js's OWN `featureKeys` — the exact strings the upgrade screen and the
// public pricing page render for that plan. Reused rather than rewritten on purpose: copy
// invented here would be a second, unreviewed source of claims about what a plan includes, and
// the first draft of it shipped one that was simply false. If a plan's benefits change, they
// change in plan-facts.js and this follows.
export const PLAN_PERK_KEYS = {
  plus: ['hdAiImages', 'basicPoses', 'AI Chat'],
  premium: ['hdAiImages', 'morePoses', 'AI Chat'],
  pro: ['aiVideosExtendVoice', 'fourKAiImages', 'allCustomPoses'],
}

// id → { emoji, plan, nameKey, taglineKey, benefitKeys, noteKey? }
//
// The name is the feature exactly as the UI the user just clicked labels it — this is only
// clearer than the generic modal if the words match the control they pressed.
//
// `noteKey` is an optional footnote for the case where a limited FREE path exists. Only add one
// where it is true: hinting at a free route that isn't there is worse than saying nothing.
export const UPGRADE_FEATURES = {
  aiCharacter: {
    emoji: '🎭',
    plan: 'pro',
    nameKey: 'upgradeGateAiCharacterName',
    taglineKey: 'upgradeGateAiCharacterTagline',
    benefitKeys: [
      'upgradeGateAiCharacterB1',
      'upgradeGateAiCharacterB2',
      'upgradeGateAiCharacterB3',
    ],
    noteKey: 'upgradeGateAiCharacterNote',
  },
  faceEnhance: {
    emoji: '✨',
    plan: 'plus',
    nameKey: 'upgradeGateFaceEnhanceName',
    taglineKey: 'upgradeGateFaceEnhanceTagline',
    benefitKeys: [
      'upgradeGateFaceEnhanceB1',
      'upgradeGateFaceEnhanceB2',
      'upgradeGateFaceEnhanceB3',
    ],
  },
  qualityExtreme: {
    emoji: '💎',
    plan: 'plus',
    nameKey: 'upgradeGateQualityExtremeName',
    taglineKey: 'upgradeGateQualityExtremeTagline',
    benefitKeys: [
      'upgradeGateQualityExtremeB1',
      'upgradeGateQualityExtremeB2',
      'upgradeGateQualityExtremeB3',
    ],
  },
  qualityMax: {
    emoji: '🔥',
    plan: 'pro',
    nameKey: 'upgradeGateQualityMaxName',
    taglineKey: 'upgradeGateQualityMaxTagline',
    benefitKeys: [
      'upgradeGateQualityMaxB1',
      'upgradeGateQualityMaxB2',
      'upgradeGateQualityMaxB3',
    ],
  },
  customPose: {
    emoji: '🧍',
    plan: 'pro',
    nameKey: 'upgradeGateCustomPoseName',
    taglineKey: 'upgradeGateCustomPoseTagline',
    benefitKeys: [
      'upgradeGateCustomPoseB1',
      'upgradeGateCustomPoseB2',
      'upgradeGateCustomPoseB3',
    ],
  },
  multiPose: {
    emoji: '🤸',
    plan: 'pro',
    nameKey: 'upgradeGateMultiPoseName',
    taglineKey: 'upgradeGateMultiPoseTagline',
    benefitKeys: [
      'upgradeGateMultiPoseB1',
      'upgradeGateMultiPoseB2',
      'upgradeGateMultiPoseB3',
    ],
  },
  pose: {
    emoji: '🧎',
    plan: 'pro',
    nameKey: 'upgradeGatePoseName',
    taglineKey: 'upgradeGatePoseTagline',
    benefitKeys: [
      'upgradeGatePoseB1',
      'upgradeGatePoseB2',
      'upgradeGatePoseB3',
    ],
  },
  video: {
    emoji: '🎬',
    plan: 'pro',
    nameKey: 'upgradeGateVideoName',
    taglineKey: 'upgradeGateVideoTagline',
    benefitKeys: [
      'upgradeGateVideoB1',
      'upgradeGateVideoB2',
      'upgradeGateVideoB3',
    ],
  },
  videoExtend: {
    emoji: '⏩',
    plan: 'pro',
    nameKey: 'upgradeGateVideoExtendName',
    taglineKey: 'upgradeGateVideoExtendTagline',
    benefitKeys: [
      'upgradeGateVideoExtendB1',
      'upgradeGateVideoExtendB2',
      'upgradeGateVideoExtendB3',
    ],
  },
  voice: {
    emoji: '🔊',
    plan: 'plus',
    nameKey: 'upgradeGateVoiceName',
    taglineKey: 'upgradeGateVoiceTagline',
    benefitKeys: [
      'upgradeGateVoiceB1',
      'upgradeGateVoiceB2',
      'upgradeGateVoiceB3',
    ],
  },
  chatFromImage: {
    emoji: '💬',
    plan: 'plus',
    nameKey: 'upgradeGateChatFromImageName',
    taglineKey: 'upgradeGateChatFromImageTagline',
    benefitKeys: [
      'upgradeGateChatFromImageB1',
      'upgradeGateChatFromImageB2',
      'upgradeGateChatFromImageB3',
    ],
    noteKey: 'upgradeGateChatFromImageNote',
  },
  // The date game's final scene. EVERY paid plan unlocks it (product decision 2026-10-02), so
  // 'plus' — the backend's `plan_unlocked` is paid = Plus/Premium/Pro and withholds the media
  // URLs otherwise. The note is true: every date is free to play up to that scene.
  // Opened from a story's locked scene, so it sells THAT scene, as AI video — Promptchan's premise
  // (2026-10-06). The earlier copy re-pitched Stories to someone already deep in one, promised
  // chapters that did not exist yet and listed perks Free already has; 0 of 69 Free players who
  // reached the lock subscribed on launch day.
  // ⚠ `plan: 'pro'` is the plan this modal SELLS, not the gate: any paid plan unlocks a scene, and
  // the server decides (date_game PAID_PLANS). Nothing calls requireFeature('dateGame') — if
  // something ever does, it must not use this entry, or Plus and Premium would be refused a scene
  // they own. The upgrade grid's Stories row says every paid plan, for anyone who wants Plus.
  dateGame: {
    emoji: '🎬',
    plan: 'pro',
    nameKey: 'upgradeGateStoriesVideoName',
    taglineKey: 'upgradeGateStoriesVideoTagline',
    benefitKeys: ['upgradeGateStoriesVideoB1'],
  },
}

// Does this plan tier satisfy the feature's gate? `tiers` is the ladder straight off useAuth, so
// there is one definition of "Plus or higher" in the app and this reads it rather than deriving
// a second one from the plan string.
export const meetsFeaturePlan = (featureId, tiers) => {
  const feature = UPGRADE_FEATURES[featureId]
  if (!feature) return true
  if (feature.plan === 'plus') return Boolean(tiers?.isPlusOrHigher)
  if (feature.plan === 'premium') return Boolean(tiers?.isPremiumOrHigher)
  return Boolean(tiers?.isStudioOrHigher) // 'pro' — "Studio" to the backend
}
